/* global paypal, braintree */
import logger from 'components/utils/logger'

// Initialise the Braintree-PayPal handshake and render the PayPal Smart Button(s).
//
// Behaviour by mode:
//   * Default (recurring vault) — single PayPal button rendered into
//     `#paypal-button-container`. flow:'vault', intent:'authorize'. Used by /checkout
//     subscribe and /review_order cart flows.
//   * options.enablePayLater === true — Lifetime checkout. Renders TWO buttons:
//     PayPal into `#paypal-paypal-button-container` and Pay Later into
//     `#paypal-paylater-button-container`. flow:'checkout', intent:'capture'.
//
// NOTE: with the standalone paypal.Buttons() API a *forced* fundingSource is
// NOT silently hidden when ineligible — .render() throws "<source> is not
// eligible" (region/amount dependent, common in sandbox). Each button is
// therefore gated on .isEligible() so an ineligible Pay Later just no-ops.
//
// The callback signature is unchanged from the previous v4 implementation:
//   isSubscribing → callback(nonce, order, plan, usedFreeTrial, userEmail)
//   else          → callback(nonce, order, deviceData)
export default (
  authorization,
  amount,
  callback,
  order,
  isSubscribing,
  plan,
  usedFreeTrial,
  deviceData,
  userEmail,
  options = {}
) =>
  braintree.client.create({ authorization }, (clientErr, clientInstance) => {
    if (clientErr) {
      logger.error(`Error creating client: ${clientErr}`)
      return
    }

    braintree.paypalCheckout.create(
      { client: clientInstance },
      (paypalCheckoutErr, paypalCheckoutInstance) => {
        if (paypalCheckoutErr) {
          logger.error(`Error creating PayPal Checkout: ${paypalCheckoutErr}`)
          return
        }

        const isLifetime = Boolean(options.enablePayLater)

        const fundingSources = isLifetime
          ? [paypal.FUNDING.PAYPAL, paypal.FUNDING.PAYLATER]
          : [paypal.FUNDING.PAYPAL]

        fundingSources.forEach((fundingSource) => {
          const containerSelector = isLifetime
            ? `#paypal-${
                fundingSource === paypal.FUNDING.PAYLATER ? 'paylater' : 'paypal'
              }-button-container`
            : '#paypal-button-container'

          const button = paypal.Buttons({
            fundingSource,
            style: { shape: 'rect', layout: 'vertical' },
            createOrder: () =>
              paypalCheckoutInstance.createPayment({
                flow: isLifetime ? 'checkout' : 'vault',
                amount,
                currency: 'USD',
                // 'authorize' (Braintree's default) for both flows: the spec's
                // createPayment example omits intent, and the backend runs the
                // actual sale server-side from the nonce. Forcing 'capture'
                // (aliased to 'sale') makes the gateway 422 the
                // create_payment_resource call → "PayPal payment options are
                // invalid."
                intent: 'authorize'
              }),
            onApprove: (data) =>
              paypalCheckoutInstance.tokenizePayment(data).then((payload) => {
                if (isSubscribing)
                  return callback(
                    payload.nonce,
                    order,
                    plan,
                    usedFreeTrial,
                    userEmail
                  )
                return callback(payload.nonce, order, deviceData)
              }),
            onError: (err) => {
              // Braintree wraps a gateway 422 as a generic "PayPal payment
              // options are invalid." — the actionable reason is nested in
              // err.details.originalError, so surface it explicitly.
              const original = err && err.details && err.details.originalError
              logger.error(
                `PayPal button error (${String(fundingSource)}): ${err}${
                  original ? ` | gateway: ${JSON.stringify(original)}` : ''
                }`
              )
            }
          })

          if (!button.isEligible()) {
            if (fundingSource === paypal.FUNDING.PAYLATER) return
            logger.error(
              `PayPal funding source not eligible: ${String(fundingSource)}`
            )
            return
          }

          button.render(containerSelector)
        })
      }
    )
  })
